import { QueryFunctionContext, useQuery } from '@tanstack/react-query';
import { compact, first, flatMap, keys, sortBy, toPairs, values } from 'lodash';
import React, { useMemo } from 'react';
import validator from 'validator';

import { Box } from '@mui/material';
import { PlotlyFigure } from 'interfaces/agentSession';
import {
  isNestedReportJobResult,
  isReportJobChartResult,
  isReportJobParquetResult,
  NestedReportJobResult,
  RenderableReportJobResult,
  ReportJobResult,
  ReportJobResultGroup,
} from 'interfaces/job';
import { useParsedBrackets } from 'utils/features/getFeaturesKeysObjectsWebWorker/useParsedBrackets';
import {
  getAllBracketsFromString,
  stringReplaceBracketsWithDisplayNames,
} from 'utils/formatBrackets/replaceBracketsWithDisplayName';
import { JobResultChart } from './JobResultChart';
import JobResultParquetTable from './JobResultParquetTable';
interface Props {
  jobId: string;
  orchestrationId?: string;
  resultGroup?: ReportJobResultGroup;
  numColumns?: number;
  spacing?: number;
  studyId?: string;
}

const buildResultGridTemplateColumns = (numColumns: number) =>
  numColumns <= 1 ? 'minmax(0, 1fr)' : `repeat(${numColumns}, minmax(0, 1fr))`;

const ChartLoader: React.FC<{
  resultEntryOrNestedResultEntry: RenderableReportJobResult | NestedReportJobResult;
  studyId?: string;
  orchestrationId?: string;
  jobId: string;
  resultKey: string;
  resultGroup: ReportJobResultGroup;
  numColumns: number;
}> = ({ resultEntryOrNestedResultEntry, studyId, orchestrationId, jobId, resultKey, resultGroup, numColumns }) => {
  const isGroup = isNestedReportJobResult(resultEntryOrNestedResultEntry);
  const [selectedGroupResultKey, setSelectedResult] = React.useState(
    isGroup ? first(keys(resultEntryOrNestedResultEntry.results)) : undefined
  );
  const groupResults = isGroup ? keys(resultEntryOrNestedResultEntry.results) : undefined;

  const resultEntry: RenderableReportJobResult | undefined = isGroup
    ? resultEntryOrNestedResultEntry.results[selectedGroupResultKey] ||
      first(values(resultEntryOrNestedResultEntry.results))
    : resultEntryOrNestedResultEntry;

  const isChartResult = isReportJobChartResult(resultEntry);

  const presignedUrl =
    isChartResult && resultEntry?.storage_url && !resultEntry?.storage_url.startsWith('s3://')
      ? resultEntry.storage_url
      : isChartResult && resultEntry?.url && !resultEntry?.url.startsWith('s3://')
      ? resultEntry.url
      : undefined;
  const resultQuery = useQuery({
    queryKey: ['presignedUrl', presignedUrl],
    queryFn: async ({ signal }: QueryFunctionContext): Promise<PlotlyFigure> => {
      const response = await fetch(presignedUrl, { signal });
      // Assume response is a JSON file
      return response.json();
    },
    enabled: Boolean(presignedUrl) && isChartResult,
  });

  const result = isChartResult ? (presignedUrl ? resultQuery?.data : resultEntry) : undefined;

  const bracketsToParse = useMemo(() => {
    const allTitles: string[] = compact([
      resultEntryOrNestedResultEntry?.title,
      resultKey,
      ...(isNestedReportJobResult(resultEntryOrNestedResultEntry)
        ? flatMap(resultEntryOrNestedResultEntry.results, (nestedResult, nestedResultKey) => [
            nestedResult?.title,
            nestedResultKey,
          ])
        : []),
    ]);
    return flatMap(allTitles, getAllBracketsFromString);
  }, [resultEntryOrNestedResultEntry, resultKey]);

  const { data: parsedBrackets, isLoading: isParsingBrackets } = useParsedBrackets({
    bracketsToParse,
    addStainToFeatureName: true,
    studyId,
    orchestrationId,
  });

  const resultError = resultQuery?.error;
  const isLoading = isChartResult && ((Boolean(presignedUrl) && resultQuery?.isLoading) || isParsingBrackets);
  const queryFailed = isChartResult && !result && !isLoading && presignedUrl;
  const showError = Boolean(resultError || queryFailed);
  if (resultError || queryFailed) {
    console.warn('Failed to query chart data', { resultError, resultGroup, resultKey, resultEntry });
  }
  const resultTitleWithoutBracketsReplacement = isGroup
    ? resultEntryOrNestedResultEntry?.title
    : resultEntry?.title || resultKey;
  const resultTitle = stringReplaceBracketsWithDisplayNames(resultTitleWithoutBracketsReplacement, parsedBrackets);

  const resultDescription = (result as ReportJobResult)?.description || resultEntry?.description;
  const links = resultEntry?.links || resultEntryOrNestedResultEntry?.links;

  if (!resultEntry) {
    return null;
  }

  const shouldSpanFullWidth = numColumns > 1 && isReportJobParquetResult(resultEntry);

  if (isReportJobParquetResult(resultEntry)) {
    return (
      <Box
        data-grid-span={shouldSpanFullWidth ? 'full' : 'single'}
        sx={{
          minWidth: 0,
          ...(shouldSpanFullWidth
            ? {
                '@media (min-width:821px)': {
                  gridColumn: '1 / -1',
                },
              }
            : {}),
        }}
      >
        <JobResultParquetTable
          resultEntry={resultEntry}
          resultTitle={resultTitle}
          resultDescription={resultDescription}
          resultKey={resultKey}
          jobId={jobId}
          studyId={studyId}
          links={links}
          selectedGroupResultKey={selectedGroupResultKey}
          groupResults={isGroup ? groupResults : undefined}
          groupKeyToTitle={(key) =>
            resultEntryOrNestedResultEntry.results[key]?.title
              ? stringReplaceBracketsWithDisplayNames(
                  resultEntryOrNestedResultEntry.results[key]?.title,
                  parsedBrackets
                )
              : validator.isHexadecimal(key)
              ? key
              : stringReplaceBracketsWithDisplayNames(key, parsedBrackets)
          }
          setSelectedResult={setSelectedResult}
        />
      </Box>
    );
  }

  return (
    <Box data-grid-span="single" sx={{ minWidth: 0 }}>
      <JobResultChart
        resultTitle={resultTitle}
        groupResults={isGroup ? groupResults : undefined}
        resultDescription={resultDescription}
        links={links}
        sourceConfig={(result as ReportJobResult | undefined)?.sourceConfig || resultEntry?.sourceConfig}
        result={result as PlotlyFigure}
        isLoading={Boolean(isLoading)}
        jobId={jobId}
        showError={showError}
        resultKey={resultKey}
        selectedGroupResultKey={selectedGroupResultKey}
        studyId={studyId}
        setSelectedResult={setSelectedResult}
        groupKeyToTitle={(key) =>
          resultEntryOrNestedResultEntry.results[key]?.title
            ? stringReplaceBracketsWithDisplayNames(resultEntryOrNestedResultEntry.results[key]?.title, parsedBrackets)
            : validator.isHexadecimal(key)
            ? key
            : stringReplaceBracketsWithDisplayNames(key, parsedBrackets)
        }
      />
    </Box>
  );
};

const JobResultsChartList: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  jobId,
  resultGroup,
  spacing = 6,
  studyId,
  orchestrationId,
  numColumns = 2,
}) => {
  const sortedResultGroup = sortBy(toPairs(resultGroup), ([resultKey, resultEntry]) => resultEntry?.index || resultKey);
  const effectiveNumColumns = sortedResultGroup.length === 1 ? 1 : numColumns;

  return (
    <Box
      data-testid="job-results-grid"
      data-grid-column-count={effectiveNumColumns}
      sx={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr)',
        gap: spacing,
        alignItems: 'start',
        ...(effectiveNumColumns > 1
          ? {
              '@media (min-width:821px)': {
                gridTemplateColumns: buildResultGridTemplateColumns(effectiveNumColumns),
              },
            }
          : {}),
      }}
    >
      {sortedResultGroup.map(([resultKey, resultEntryOrNestedResultEntry]) => (
        <ChartLoader
          key={resultKey}
          resultEntryOrNestedResultEntry={resultEntryOrNestedResultEntry}
          studyId={studyId}
          orchestrationId={orchestrationId}
          jobId={jobId}
          resultKey={resultKey}
          resultGroup={resultGroup!}
          numColumns={effectiveNumColumns}
        />
      ))}
    </Box>
  );
};

export default JobResultsChartList;
